import { useEffect, useState } from "react";
import { MainPageBase } from "../page-bases/MainPageBase";
import { Box, Stack } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import { TRowItem } from "../../components/Table/TableRow";
import { useMangeTableItems } from "../../hooks/useManageTableItems";
import { usePagination } from "../../hooks/usePagination";
import { assetBatchDistributionApi } from "../../services/distribution/asset-distribution";
import { actionDialogStore, alertDialogStore } from "../../store/dialogs";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { SimpleLabel } from "../../components/Label/SimpleLabel";
import { updateBatchDistributionCart } from "../../store/reducers/distribution/distributedRecordStore";
import { AssetLogsFilter } from "../../common-ui/page-filters";
import {
  IAssetRecord,
  ICreateAssetAssignment,
  IPagination,
  IQueryPerams,
} from "../../config/types";
import { formatDate } from "../../utils/common";
import DamsTable from "../../components/Table/CollapsibleTable";
import { UserSelectInput } from "../../common-ui/select-input-models/UserSelectInput";
import { Field } from "../../utils/formSchema";
import { useDmsForm } from "../../hooks/useDmsFrom";
import ItemInput from "../../components/Input/ItemInput";
import { assignmentApi } from "../../services/assets/asset-assignment";
import appRoutePath from "../../config/path";
import { BadgeLabel } from "../../components/Label";
import { AssetAssignmentStatus } from "../../config/constants/common";

function AssignAssetToUser() {
  const [dataset, setDataset] = useState<TRowItem[]>([]);
  const { id } = useParams<{ id: string }>();
  const entryDetail = useAppSelector((data) => data.batchDistributionCart);
  const [userName,setUserName]=useState('')
  const fetchAssetsFromDistribution = () =>
    assetBatchDistributionApi.getAssetFromDistribution(id ?? "");
  const {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    paginateToPageNumber,
    updatePaginationInitState,
  } = usePagination<any>(fetchAssetsFromDistribution);
  const dispatch = useAppDispatch();
  const { selectedRowItems, toggleRow, toggleSelectAllRow } =
    useMangeTableItems(dataset, false);

  const {
    form,
    updateForm,
    fieldHasError,
    setSubmitAttempt,
    updateInputField,
    onFormSubmit,
  } = useDmsForm<ICreateAssetAssignment>({
    remarks: "",
    user_id: "",
    asset_id: "",
  });
  const [querySet, setQuerySet] = useState<IQueryPerams>({ is_deleted: false });
  const populateDataSet = (data: IAssetRecord[], pagination?: IPagination) => {
    const formatData = data.map((data) => ({
      ...data,
      brand:`${data.brand}(${data.model})`,
      date_received: formatDate(data.date_received),
      warranty_in_months: data.warranty_in_months ?? "Not Stated",
      asset_meta_raw: data.asset_meta
        ? data.asset_meta.map(
            (item) => ` ${item.asset_tag}=${item.meta_value} `
          )
        : [],
    }));
    setDataset(formatData);
    updatePaginationInitState(pagination);
  };

  const fetchData = async () => {
    const result = await assetBatchDistributionApi.getAssetFromDistribution(
      id ?? ""
    );
    if (result?.data) {
      populateDataSet(result.data, result.pagination);
    }
    const distributionDetail =
      await assetBatchDistributionApi.getBatchDistribution(id ?? "");
    if (distributionDetail.data) {
      dispatch(updateBatchDistributionCart(distributionDetail.data));
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigate = useNavigate();
  function cancelAddItem() {
    navigate(appRoutePath.ASSET_IN_MY_POSSESSION_PAGE);
  }

  function handleUserSelection(item: Field,label?:string) {
    setUserName(label??'')
    updateForm((p) => ({ ...p, user_id: item }));
  }



  async function submitForm(assetId:string) {
    setSubmitAttempt(true);
    const { data, isValid } = onFormSubmit({ excludeFields: ["asset_id"] });
    if (isValid) {
      const result = await assignmentApi.createAssetAssignment({
        ...data,
        asset_id: assetId,
      });
      if(result.success){
        await fetchData();
      }
      alertDialogStore.open({
        title: "Asset Assignment",
        subtitle: result.message,
        isSuccess: result.success,
      });
    }
    
  }


  const BeginAssignmentButton = ({ item }: { item: TRowItem }) => {
  
    const record=item as IAssetRecord
    const canAssign=record.assignment_status===AssetAssignmentStatus.not_assigned
    const handleTagAction = async () => {

      if(!userName){
        alertDialogStore.open({
          title: "Required Action",
          subtitle: "Please select User and continue",
          isSuccess: false,
          rightBtnTxt:'okay'
        });
        return;

      }

      actionDialogStore.open({
        title: "Asset Assignment",
        subtitle: `click continue to assign asset to ${userName}`,
        onRightBtnPress:()=>submitForm(record.id) ,
      });
    }
    return (
      <Box>
        {
          canAssign && 
        
        <BadgeLabel
          onClick={handleTagAction}
          title={"Assign Asset"}
          isLarge
          colorType="blue"
        />
  }
      </Box>
    );
  };
  if (!entryDetail.id) {
    return null;
  }

  
  return (
    <MainPageBase
      pageState={"main"}
      title={`Assign Asset to User`}
      subtitle={`Select a user and assign an asset from the distribution list.`}
      cancelBtnClick={() => cancelAddItem()}
    >
      <Box
        height={`100%`}
        display={"flex"}
        alignItems={""}
        justifyContent={"center"}
      >
        <Stack width={"100%"}>
          <Stack
            spacing={1.5}
            marginBottom={2}
            direction={"column"}
            width={400}
          >
            <SimpleLabel title=" Details" />
            <UserSelectInput
              label="User"
              placeholder="Select User Name"
              handleChange={handleUserSelection}
              value={form.user_id.value}
              error={!form.user_id.isValid && form.submitAttempt}
            />
            <ItemInput
              label="Remarks"
              placeholder="Long text"
              handleChange={updateInputField("remarks")}
              value={form.remarks.value}
              error={fieldHasError("remarks")}
              errorMessage={form.remarks.errorMessage}
              size="full"
              multiline
            />
          </Stack>

          <AssetLogsFilter<IAssetRecord[]>
            ftn={fetchAssetsFromDistribution}
            updateDataSet={populateDataSet}
            updateQuerySet={setQuerySet}
            isAssigned={false}
            isDeleted={false}
          />

          <DamsTable
            tableTitle={"Assets Available for Assignment"}
            tableSubtitle={`Select an asset to assign to the chosen user.`}
            tableTitleBadge={
              selectedRowItems.length === 0
                ? ""
                : ` ${selectedRowItems.length} selected`
            }
            tableHeaders={columns}
            hiddenKeys={hkeys}
            hiddenLabels={hcolumns}
            data={dataset}
            tableKeys={tkeys}
            selectedRowItems={selectedRowItems}
            onTableItemSelect={toggleRow}
            toggleSelectAllRow={toggleSelectAllRow}
            hasHeaderCheckBox={false}
            maxRowCells={6}
            hasPagination
            paginationState={pagination}
            onNextPage={paginateToNextPage(querySet, populateDataSet)}
            onPreviousPage={paginateToPreviousPage(querySet, populateDataSet)}
            setActivePage={paginateToPageNumber(populateDataSet, querySet)}
            extraRowComponent={BeginAssignmentButton}
          />
        </Stack>
      </Box>
    </MainPageBase>
  );
}

const tkeys = [
  "asset_category_name",
  "serial_number",
  "brand",
  "embossment_number",
  "assigned",
  "assignment_status",
  "date_received",
];

const columns = [
  "Asset Category",
  "Serial Number",
  "Brand (Model)",
  "Embossment Number",
  "Assignment Status",
  "Assignment Stage",
  "Date Received",
];
const hkeys : (keyof IAssetRecord)[] = [
  "batch_number",
  "current_location",
  "current_location_domain",
  "description",
  "warranty_in_months",
  "asset_meta_raw"];

const hcolumns = [
  "Batch Number",
  "Current Location",
  "current location domain",
  "Description",
  "Warranty (months)", "Additional Information"];

export { AssignAssetToUser };
